import React from 'react';
import { BaseModal } from '@/components/shared';
import { contagemPresenca, pendenciasOnboarding, type SessaoOnboarding } from './onboardingPresenca';

interface SessoesOnboardingModalProps {
  sessoes: SessaoOnboarding[];
  isOpen: boolean;
  onClose: () => void;
  onAbrir: (sessao: SessaoOnboarding) => void;
  onNova: () => void;
}

const dataBR = (data: string) => data.split('-').reverse().slice(0, 2).join('/');

/** Histórico das sessões e quem ainda não completou (faltou ou ficou só parte) — pra chamar na próxima turma. */
export default function SessoesOnboardingModal({ sessoes, isOpen, onClose, onAbrir, onNova }: SessoesOnboardingModalProps) {
  const pendentes = pendenciasOnboarding(sessoes);
  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title="Sessões de onboarding" maxWidth="max-w-6xl" fullHeight={false}>
      <div className="flex flex-wrap items-start gap-5 py-4">
        <section className="min-w-0 flex-[999_1_520px] overflow-hidden rounded-lg border border-gray-200">
          <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
            <p className="font-montserrat font-semibold text-gray-900">{sessoes.length} sessão(ões)</p>
            <button type="button" className="btn-primary text-sm" onClick={onNova}>
              + Registrar sessão
            </button>
          </div>
          {sessoes.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-gray-500">Nenhuma sessão registrada ainda.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2 font-semibold">Data</th>
                    <th className="px-2 py-2 font-semibold">Apresentado por</th>
                    <th className="px-2 py-2 font-semibold">Versão</th>
                    <th className="px-2 py-2 font-semibold">Presença</th>
                    <th className="px-4 py-2">
                      <span className="sr-only">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sessoes.map((sessao) => {
                    const conta = contagemPresenca(sessao.participantes);
                    return (
                      <tr key={sessao.id}>
                        <td className="px-4 py-3">
                          <b>{dataBR(sessao.data)}</b>{' '}
                          {sessao.inicio && (
                            <span className="text-gray-500">
                              {sessao.inicio}
                              {sessao.fim ? `–${sessao.fim}` : ''}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-3">{sessao.apresentador.name}</td>
                        <td className="px-2 py-3">v{sessao.versao}</td>
                        <td className="px-2 py-3">
                          {[
                            conta.completa ? <span key="c" className="font-semibold text-green-700">{conta.completa} completo(s)</span> : null,
                            conta.parcial ? <span key="p" className="font-semibold text-amber-800">{conta.parcial} parcial(is)</span> : null,
                            conta.faltou ? <span key="f" className="font-semibold text-red-700">{conta.faltou} faltou(aram)</span> : null,
                          ]
                            .filter(Boolean)
                            .reduce<React.ReactNode[]>((acc, item, i) => (i === 0 ? [item] : [...acc, ' · ', item]), [])}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button type="button" className="font-semibold text-primary-700 hover:underline" onClick={() => onAbrir(sessao)}>
                            Abrir
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <aside aria-label="Ainda não completaram" className="min-w-0 flex-[1_1_300px] overflow-hidden rounded-lg border border-gray-200 lg:max-w-[380px]">
          <div className="border-b border-gray-100 px-4 py-3">
            <p className="font-montserrat font-semibold text-gray-900">Ainda não completaram</p>
            <p className="text-xs text-gray-500">Quem faltou ou ficou só parte — pra chamar na próxima turma.</p>
          </div>
          {pendentes.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-gray-500">Ninguém pendente.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {pendentes.map((pessoa) => (
                <li key={pessoa.colaboradorId} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
                  <span className="min-w-0">
                    <span className="block font-semibold text-gray-900">{pessoa.nome}</span>
                    <span className="text-xs text-gray-500">
                      {dataBR(pessoa.data)} · {pessoa.detalhe}
                    </span>
                  </span>
                  <span
                    className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                      pessoa.presenca === 'faltou' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    {pessoa.presenca === 'faltou' ? 'Não veio' : 'Parcial'}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="bg-gray-50 px-4 py-2 text-xs text-gray-500">Sai da lista quando participa de tudo numa sessão seguinte.</p>
        </aside>
      </div>
    </BaseModal>
  );
}
